import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, GcpResource, HealthStatus } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService, MonitoringFinding, MonitoringResourceType } from '../../core/services/monitoring.service';
import { serviceCardById } from '../../core/cloud-catalog';
import { regionLabel } from '../../core/gcp-regions';
import { ALL_REGIONS_SCOPE } from '../../core/aws-regions';
import { CloudFilters, FacetDef, SortOption, parseFiltersFromParams, filtersToParams } from './cloud-filter.model';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';
import { CloudTab } from './cloud-tabs.component';

type Tab = 'overview' | 'resources' | 'activity';

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['status', 'region', 'monitoring'];
const DAY_MS       = 24 * 60 * 60 * 1000;

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'issues-desc',     label: 'Most issues'     },
  { value: 'region-asc',      label: 'Region A → Z'    },
  { value: 'monitored-first', label: 'Monitored first' },
];

/**
 * GCP service detail page — the GCP analog of the AWS/Azure `/cloud/:service`
 * detail pages, for every GCP service (one generic component, selected by route
 * `data.id`). It mirrors the Azure page's information architecture and reuses
 * the SAME shared building blocks (cloud-filter-bar toolbar, cloud-tabs,
 * cloud-activity-feed, status-badge, cloudDate, the cloud-filter /
 * cloud-activity models) so AWS, Azure and GCP read as one product system.
 *
 * Native to GCP: resources carry a project + region + type, global services
 * (Cloud CDN) are discovered project-wide, and the scope is a project + region
 * (not an account + region / subscription + location).
 */
@Component({
  selector: 'app-gcp-service-detail',
  templateUrl: './gcp-service-detail.component.html',
})
export class GcpServiceDetailComponent implements OnInit, OnDestroy {
  id = '';
  serviceType = '';
  title = 'GCP service';
  icon = 'cloud';
  metricLabel = 'Resources';
  isGlobal = false;

  /** Active scope, inherited from the overview via ?account= / ?region=. */
  project = '';
  projectLabel = '';
  region = ALL_REGIONS_SCOPE;

  selectedTab: Tab = 'overview';
  resources: GcpResource[] = [];
  loading = true;
  error: string | null = null;
  refreshing = false;

  findings: MonitoringFinding[] = [];
  activity: ActivityItem[] = [];
  activityLoading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };
  readonly pageSize = 10;
  currentPage = 1;
  readonly sortOptions = SORT_OPTIONS;

  /** Selected resource for the side detail panel (?resource=). */
  selectedResourceId: string | null = null;

  readonly toggling = new Set<string>();
  toggleError: string | null = null;
  readonly regionLabel = regionLabel;

  get tabs(): CloudTab[] {
    return [
      { id: 'overview',  label: 'Overview'      },
      { id: 'resources', label: this.metricLabel },
      { id: 'activity',  label: 'Activity'      },
    ];
  }

  private qpSub?: Subscription;
  private loadSub?: Subscription;
  private connSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.data['id'] as string;
    const card = serviceCardById(id);
    this.id = id;
    this.serviceType = card?.serviceType ?? '';
    this.title = card?.name ?? 'GCP service';
    this.icon = card?.icon ?? 'cloud';
    this.metricLabel = card?.metricLabel ?? 'Resources';
    this.isGlobal = card?.scope === 'global';

    this.cloud.loadMonitoringState().subscribe();
    this.applyScope();
    this.resolveProjectLabel();
    this.loadData(false);
    this.loadFindings();

    this.qpSub = this.route.queryParams.subscribe(params => {
      const newFilters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      if (JSON.stringify(newFilters) !== JSON.stringify(this.filters)) this.currentPage = 1;
      this.filters = newFilters;
      this.selectedResourceId = (params['resource'] as string | undefined) ?? null;

      const changed = this.applyScope();
      if (changed) { this.resolveProjectLabel(); this.loadData(false); this.loadFindings(); }
    });
  }

  /** Reads the project + region from the URL and sets the shared scope. Returns true on change. */
  private applyScope(): boolean {
    const account = this.route.snapshot.queryParamMap.get('account') ?? this.cloud.getAccount();
    const region = this.route.snapshot.queryParamMap.get('region') ?? ALL_REGIONS_SCOPE;
    const changed = account !== this.project || region !== this.region;
    this.project = account;
    this.region = region;
    this.cloud.setAccount(account);
    this.cloud.setScope(region);
    return changed;
  }

  private resolveProjectLabel(): void {
    this.projectLabel = this.project;
    this.connSub?.unsubscribe();
    this.connSub = this.cloud.getGcpConnection().subscribe(conn => {
      const proj = conn?.projects.find(p => p.projectId === this.project);
      this.projectLabel = proj?.label || this.project;
    });
  }

  private loadData(bypass: boolean): void {
    this.loading = !bypass;
    this.refreshing = bypass;
    this.loadSub?.unsubscribe();
    this.loadSub = this.cloud.getGcpResourcesResult(this.serviceType).subscribe(res => {
      this.resources = res.resources;
      this.error = res.error;
      this.loading = false;
      this.refreshing = false;
    });
  }

  private loadFindings(): void {
    this.activityLoading = true;
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(all => {
      this.findings = all.filter(f => f.serviceType === (this.serviceType as MonitoringResourceType));
      this.activity = sortActivity(findingsToActivity(all, this.serviceType as MonitoringResourceType));
      this.activityLoading = false;
    });
  }

  refresh(): void {
    if (this.refreshing) return;
    this.loadData(true);
    this.loadFindings();
  }

  // ── Scope labels ──────────────────────────────────────────────────────────

  get regionScopeLabel(): string {
    if (this.isGlobal) return 'Global';
    if (this.region === ALL_REGIONS_SCOPE) return 'All regions';
    return this.regionLabel(this.region);
  }

  // ── Summary counts (Overview) ──────────────────────────────────────────────

  get totalCount(): number { return this.resources.length; }
  get monitoredCount(): number { return this.resources.filter(r => this.isMonitored(r)).length; }
  get distinctRegionCount(): number { return new Set(this.resources.map(r => r.location)).size; }
  get issues24hTotal(): number {
    const cutoff = this.now() - DAY_MS;
    return this.findings.filter(f => Date.parse(f.createdAt) >= cutoff).length;
  }

  /** Open findings for a resource within the last 24h (drives the Issues column). */
  issueCount(resourceId: string): number {
    const cutoff = this.now() - DAY_MS;
    return this.findings.filter(f => f.resourceId === resourceId && Date.parse(f.createdAt) >= cutoff).length;
  }

  private now(): number { return Date.parse(new Date().toISOString()); }

  // ── Status mapping (GCP resource state → shared health badge) ──────────────

  statusHealth(r: GcpResource): HealthStatus {
    const s = (r.status || '').toLowerCase();
    if (['running', 'ready', 'active', 'available', 'succeeded', 'online', 'enabled'].includes(s)) return 'healthy';
    if (['failed', 'stopped', 'terminated', 'suspended', 'error'].includes(s)) return 'critical';
    if (['creating', 'updating', 'deleting', 'provisioning', 'starting', 'pending', 'staging'].includes(s)) return 'warning';
    return 'unknown';
  }

  /** Resource type — the GCP-native "type" signal. */
  resourceType(r: GcpResource): string { return r.type || '—'; }

  // ── Facets (dynamic: regions + statuses derived from data) ─────────────────

  private _facetsRef: GcpResource[] | null = null;
  private _facetsCache: FacetDef[] = [];

  get facets(): FacetDef[] {
    if (this._facetsRef === this.resources) return this._facetsCache;
    this._facetsRef = this.resources;
    const regions = [...new Set(this.resources.map(r => r.location))].sort();
    const statuses  = [...new Set(this.resources.map(r => this.statusHealth(r)))];
    const STATUS_LABELS: Record<string, string> = { healthy: 'Healthy', warning: 'Transitional', critical: 'Not running', unknown: 'Unknown', idle: 'Idle' };
    this._facetsCache = [
      { key: 'status', label: 'Status', options: statuses.map(s => ({ value: s, label: STATUS_LABELS[s] ?? s })) },
      { key: 'region', label: 'Region', options: regions.map(r => ({ value: r, label: this.regionLabel(r) !== r ? `${r} (${this.regionLabel(r)})` : r })) },
      { key: 'monitoring', label: 'Monitoring', options: [
        { value: 'monitored',   label: 'Monitored' },
        { value: 'unmonitored', label: 'Not monitored' },
      ] },
    ];
    return this._facetsCache;
  }

  // ── Filtered + sorted list ─────────────────────────────────────────────────

  get filteredResources(): GcpResource[] {
    let result = this.resources;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(r => r.name.toLowerCase().includes(q) || (r.project ?? '').toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(r => statuses.includes(this.statusHealth(r)));

    const regions = this.filters.facets['region'] ?? [];
    if (regions.length) result = result.filter(r => regions.includes(r.location));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(r => {
        const mon = this.isMonitored(r);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }
    return this.sorted(result, this.filters.sort);
  }

  private sorted(rs: GcpResource[], sort: string): GcpResource[] {
    return [...rs].sort((a, b) => {
      switch (sort) {
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'region-asc':    return a.location.localeCompare(b.location) || a.name.localeCompare(b.name);
        case 'issues-desc':   return this.issueCount(b.id) - this.issueCount(a.id) || a.name.localeCompare(b.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a) ? 0 : 1;
          const bM = this.isMonitored(b) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default:              return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.currentPage = 1;
    this.router.navigate([], { relativeTo: this.route, queryParams: filtersToParams(f, FACET_KEYS), queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  get totalPages(): number { return Math.max(1, Math.ceil(this.filteredResources.length / this.pageSize)); }
  get pageNumbers(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
  get pagedResources(): GcpResource[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredResources.slice(start, start + this.pageSize);
  }
  get pageStart(): number { return this.filteredResources.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; }
  get pageEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredResources.length); }
  goToPage(p: number): void { this.currentPage = Math.max(1, Math.min(p, this.totalPages)); }
  prevPage(): void { this.goToPage(this.currentPage - 1); }
  nextPage(): void { this.goToPage(this.currentPage + 1); }

  // ── Monitoring ─────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(r: GcpResource): boolean {
    return this.cloud.isGcpMonitored(this.serviceType, r.id, this.project, this.isGlobal ? undefined : this.region);
  }
  isToggling(r: GcpResource): boolean { return this.toggling.has(r.id); }

  toggle(r: GcpResource): void {
    if (this.toggling.has(r.id)) return;
    const next = !this.isMonitored(r);
    if (next && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    const reg = this.isGlobal ? undefined : (r.location || (this.region !== ALL_REGIONS_SCOPE ? this.region : undefined));
    this.toggling.add(r.id);
    this.toggleError = null;
    this.cloud.toggleGcpMonitoring(this.serviceType, r.id, next, reg, { name: r.name }).subscribe({
      next: () => { this.toggling.delete(r.id); },
      error: (err: { status?: number; error?: { code?: string; message?: string } }) => {
        this.toggling.delete(r.id);
        if (err.status === 403) {
          this.toggleError = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
        } else {
          this.toggleError = err?.error?.message ?? 'Failed to update monitoring for this resource.';
        }
      },
    });
  }

  // ── Side detail panel ──────────────────────────────────────────────────────

  selectResource(id: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { resource: id }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { resource: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  get selectedResource(): GcpResource | null {
    return this.resources.find(r => r.id === this.selectedResourceId) ?? null;
  }
  get selectedResourceFindings(): MonitoringFinding[] {
    if (!this.selectedResourceId) return [];
    return this.findings
      .filter(f => f.resourceId === this.selectedResourceId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'resources';
    this.selectResource(item.resourceId);
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.connSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }
}
